import type {
  ApiInfrastructureErrors,
  BackendApiErrorUnion,
} from '@vs-shop/engine/data/error/backendApi/_internal/createBackendApiError';
import { createBackendApiError } from '@vs-shop/engine/data/error/backendApi/_internal/createBackendApiError';
import { liftRequest } from '@vs-shop/engine/request/backendApi/context';
import { BackendApi } from '../../../service/api/BackendApi';
import type { AuthToken } from '@vs-shop/engine/service/auth/AuthToken';
import { ApiModule } from '@vs-shop/engine/service/logger/ApiModule';
import type { LoggerConfig } from '@vs-shop/engine/service/logger/LoggerConfig';
import { addResponseStatusToSpan } from '@vs-shop/engine/utils/httpTracing';
import { logApiOperation } from '@vs-shop/engine/utils/logger/apiLogger';
import { schemaBodyJsonClean } from '@vs-shop/schemas/transform/schemaBodyJsonClean';
import type { StripJsonLd } from '@vs-shop/schemas/transform/withoutJsonLd';
import type { DateTime, Schema as SchemaType } from 'effect';
import { Effect, Request, RequestResolver, Schema } from 'effect';
import type * as HttpClient from 'effect/unstable/http/HttpClient';
import * as HttpClientResponse from 'effect/unstable/http/HttpClientResponse';

const ENDPOINT = '/api/v2/shop/product-attribute-group';
const HTTP_METHOD = 'get';
export type MakeListProductAttributeGroupsDeps<ProductAttributeGroups extends SchemaType.Top> = {
  readonly schemas: {
    readonly productAttributeGroups: ProductAttributeGroups;
  };
};

export const makeListProductAttributeGroups = <ProductAttributeGroups extends SchemaType.Top>(
  deps: MakeListProductAttributeGroupsDeps<ProductAttributeGroups>
) => {
  const ListProductAttributeGroupsError = createBackendApiError({
    domain: 'ProductAttributeGroup',
    endpoint: 'List',
    path: ENDPOINT,
    identifierField: 'page',
  });

  type ProductAttributeGroupRequestError = BackendApiErrorUnion<
    typeof ListProductAttributeGroupsError
  >;

  const listProductAttributeGroupsFetch = Effect.fn(`http.${HTTP_METHOD} ${ENDPOINT}`, {
    kind: 'client',
    attributes: {
      'http.request.method': HTTP_METHOD,
    },
  })(function* () {
    const apiClient = yield* BackendApi;
    return yield* apiClient
      .path(ENDPOINT)
      .method(HTTP_METHOD)
      .accept('application/ld+json')
      .fetch({ body: {} });
  });

  const listProductAttributeGroups = () =>
    listProductAttributeGroupsFetch().pipe(
      Effect.map((response) => response.response),
      Effect.tap(addResponseStatusToSpan),
      Effect.withSpan('ValidateResponse'),
      Effect.flatMap(
        HttpClientResponse.matchStatus({
          '2xx': (response) =>
            schemaBodyJsonClean(
              Schema.Struct({
                'hydra:member': Schema.Array(deps.schemas.productAttributeGroups),
              })
            )(response),
          404: (response) =>
            Effect.fail(new ListProductAttributeGroupsError.NotFoundError({ page: 1, response })),
          orElse: (response) =>
            Effect.fail(new ListProductAttributeGroupsError.ApiRequestError({ page: 1, response })),
        })
      ),
      Effect.map((apiResponse) => apiResponse['hydra:member']),
      Effect.tap((result) =>
        logApiOperation(
          ApiModule.ProductAttributeGroupsList,
          'Debug',
          `Successfully resolved ${String(result.length)} product attribute groups`
        )
      ),
      Effect.withSpan('api.productAttributeGroups.list', {
        kind: 'internal',
        attributes: {},
      })
    );

  type ListProductAttributeGroupsRequest = {
    readonly _tag: 'ListProductAttributeGroupsRequest';
  } & Request.Request<
    readonly StripJsonLd<ProductAttributeGroups['Type']>[],
    ProductAttributeGroupRequestError | ApiInfrastructureErrors,
    BackendApi | AuthToken | HttpClient.HttpClient | LoggerConfig | DateTime.CurrentTimeZone
  >;

  const ListProductAttributeGroupsRequest = Request.tagged<ListProductAttributeGroupsRequest>(
    'ListProductAttributeGroupsRequest'
  );

  const ListProductAttributeGroupsResolver = RequestResolver.fromEffect(
    liftRequest((_request: ListProductAttributeGroupsRequest) => listProductAttributeGroups())
  );

  const requestProductAttributeGroups = () =>
    Effect.request(ListProductAttributeGroupsRequest(), ListProductAttributeGroupsResolver);

  return { requestProductAttributeGroups, ListProductAttributeGroupsError };
};
